import React, { useState, useEffect } from 'react';
import {
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  BackwardIcon,
  SpeakerWaveIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/solid';
import usePlayerStore from '../../stores/playerStore';
import useEventStore from '../../stores/eventStore';
import { useRoleStore } from '../../stores/roleStore';
import useReactionStore from '../../stores/reactionStore';
import SettingsModal from '../modals/SettingsModal';
import TrackReactionBar from '../reactions/TrackReactionBar';
import { loadedFractionToPercent, readLoadedFraction } from '../../utils/bufferProgress';
import { expectedTime, isRemotePlaybackActive, parseDurationSeconds } from '../../utils/playbackClock';
import { formatTrackTitleLine } from '../../utils/trackDisplay';
import { formatAddedByLabel } from '../../utils/queueAttribution';

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
}

const BUFFER_POLL_MS = 750;

export default function FloatingDock() {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [bufferedPercent, setBufferedPercent] = useState(0);
  const [guestDisplayTime, setGuestDisplayTime] = useState(0);
  const [hostPlaybackLive, setHostPlaybackLive] = useState(false);

  const {
    isPlaying,
    currentSong,
    volume,
    currentTime,
    duration,
    remotePositionSeconds,
    remoteUpdatedAtMs,
    playerInstance,
    setVolume,
    setCurrentTime,
    setDuration,
    playPause,
    skipSong,
    previousSong,
    seekTo,
  } = usePlayerStore();

  const eventData = useEventStore((state) => state.eventData);
  const userId = useRoleStore((state) => state.userId);
  const userRole = useRoleStore((state) => state.userRole) || '';
  const canControl = userRole === 'host';
  const isGuest = userRole !== 'host';

  useEffect(() => {
    useReactionStore.getState().bindScope({
      context: 'event',
      contextId: eventData?.id || null,
      userId,
    });
  }, [eventData?.id, userId]);

  useEffect(() => {
    let interval;
    if (playerInstance && isPlaying) {
      interval = setInterval(() => {
        try {
          const time = playerInstance.getCurrentTime?.() || 0;
          const dur = playerInstance.getDuration?.() || 0;
          setCurrentTime(time);
          setDuration(dur);
        } catch (e) {
          console.error('Error fetching player time:', e);
        }
      }, 500);
    }
    return () => clearInterval(interval);
  }, [playerInstance, isPlaying, setCurrentTime, setDuration]);

  useEffect(() => {
    setBufferedPercent(0);
  }, [currentSong?.id]);

  useEffect(() => {
    if (!isGuest) return undefined;

    const tick = () => {
      const live = isRemotePlaybackActive({ isPlaying, updatedAtMs: remoteUpdatedAtMs });
      setHostPlaybackLive(live);
      setGuestDisplayTime(expectedTime(remotePositionSeconds, live, remoteUpdatedAtMs));
    };

    tick();
    if (!isPlaying) return undefined;
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [isGuest, isPlaying, remotePositionSeconds, remoteUpdatedAtMs, currentSong?.id]);

  useEffect(() => {
    if (isGuest || !playerInstance) {
      setBufferedPercent(0);
      return undefined;
    }

    const readBuffer = () => {
      const fraction = readLoadedFraction(playerInstance);
      if (fraction == null) return;
      const next = loadedFractionToPercent(fraction);
      if (next == null) return;
      setBufferedPercent(next);
    };

    readBuffer();
    const interval = setInterval(readBuffer, BUFFER_POLL_MS);
    return () => clearInterval(interval);
  }, [isGuest, playerInstance, currentSong?.id]);

  const showPlaying = isGuest ? hostPlaybackLive : isPlaying;
  const displayTime = isGuest ? guestDisplayTime : currentTime;
  const displayDuration = isGuest
    ? parseDurationSeconds(currentSong?.duration) || duration
    : duration;
  const playedPercent = displayDuration > 0
    ? Math.min(100, Math.max(0, (displayTime / displayDuration) * 100))
    : 0;

  const handleSeek = (e) => {
    if (!canControl) return;
    seekTo(parseFloat(e.target.value));
  };

  const handlePlayPause = () => {
    if (!canControl) return;
    playPause();
  };

  const handleSkip = () => {
    if (!canControl) return;
    skipSong();
  };

  const handlePrevious = () => {
    if (!canControl) return;
    previousSong();
  };

  const titleLine = formatTrackTitleLine(currentSong);

  return (
    <>
<div className="sticky bottom-0 z-10 bg-gradient-to-b from-black/80 to-black/40 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
   <div className="flex flex-col space-y-2 mx-4">

          <div className="flex items-center justify-between gap-3">
            {currentSong ? (
              <div className="flex min-w-0 flex-1 items-center space-x-4">
                <div className="relative shrink-0">
                  <div className="w-16 h-16 rounded-lg overflow-hidden shadow-lg border border-white/10">
                    <img
                      src={currentSong.thumbnail}
                      alt={currentSong.title}
                      className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  {!canControl && currentSong && (
                    <div className="absolute -top-2 -right-2">
                      {showPlaying ? (
                        <div className="flex items-center gap-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs px-3 py-1 rounded-full shadow-lg border border-white/20">
                          <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                          <span className="font-medium">Live</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 bg-gradient-to-r from-orange-600 to-amber-600 text-white text-xs px-3 py-1 rounded-full shadow-lg border border-white/20">
                          <div className="w-2 h-2 rounded-full bg-white/80" />
                          <span className="font-medium">Paused</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="truncate text-base font-medium text-white">{titleLine}</span>
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm text-white/70">
                      {formatAddedByLabel(currentSong.addedBy, eventData?.participants)}
                    </span>
                    {!canControl && currentSong && (
                      <span className="whitespace-nowrap rounded-full border border-indigo-500/30 bg-white/10 px-2.5 py-0.5 text-xs font-medium text-indigo-300">
                        {showPlaying ? 'Live with host' : 'Synced with host'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-white/60">No track playing</div>
            )}

            <div className="flex shrink-0 items-center space-x-4">
              {canControl && (
                <button onClick={handlePrevious} className="p-2 hover:bg-white/10 rounded-full" title="Previous">
                  <BackwardIcon className="w-6 h-6 text-white/80" />
                </button>
              )}

              <button
                onClick={handlePlayPause}
                className={`p-3 rounded-full transition-colors ${
                  canControl ? 'bg-cyan-500/20 hover:bg-cyan-500/30' : 'bg-gray-500/20'
                }`}
                disabled={!canControl}
                title={showPlaying ? 'Pause' : 'Play'}
              >
                {showPlaying ? (
                  <PauseIcon className={`w-8 h-8 ${canControl ? 'text-cyan-500' : 'text-gray-500'}`} />
                ) : (
                  <PlayIcon className={`w-8 h-8 ${canControl ? 'text-cyan-500' : 'text-gray-500'}`} />
                )}
              </button>

              {canControl && (
                <button onClick={handleSkip} className="p-2 hover:bg-white/10 rounded-full" title="Skip">
                  <ForwardIcon className="w-6 h-6 text-white/80" />
                </button>
              )}
            </div>
          </div>

          {currentSong && (
            <div className="flex items-center space-x-2">
              <span className="text-xs text-white/60">{formatTime(displayTime)}</span>
              <div className="relative flex-1 h-4">
                <div className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 overflow-hidden rounded-lg bg-white/15">
                  {canControl && (
                    <div
                      className="absolute inset-y-0 left-0 bg-white/55"
                      style={{ width: `${bufferedPercent}%` }}
                    />
                  )}
                  <div
                    className="absolute inset-y-0 left-0 bg-cyan-500"
                    style={{ width: `${playedPercent}%` }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max={displayDuration || 0}
                  value={displayTime}
                  onChange={handleSeek}
                  disabled={!canControl}
                  className="seek-bar absolute inset-0 z-10 w-full disabled:cursor-default"
                />
              </div>
              <span className="text-xs text-white/60">{formatTime(displayDuration)}</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            {canControl && (
              <div className="flex items-center space-x-2">
                <SpeakerWaveIcon className="w-5 h-5 text-white/60" />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(e) => setVolume(parseInt(e.target.value, 10))}
                  className="w-24"
                  aria-label="Volume"
                />
              </div>
            )}

            {currentSong && <TrackReactionBar track={currentSong} />}

            {canControl && (
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="p-2 text-white/60 hover:bg-white/10 rounded-full transition-colors"
                title="Settings"
                aria-label="Settings"
              >
                <Cog6ToothIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {isSettingsOpen && <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />}
    </>
  );
}
